import React, { SyntheticEvent } from 'react';
import { ActionButton, ActionButtonOutline, Column } from './TaskTable.styles';
import { Person, Task, TaskStatus } from './types';
import { hasTaskAction } from './taskActionLookup';
import { openModal } from 'modal-core-components';
import ResolveModal, { ManualResolution } from './ResolveModal';
import { FetchResult } from '@apollo/client';
import { type TaskManagerContext, useTaskManagerContext } from './TaskManagerContext';

const showResolveModal = (
  task: Task,
  resolveTask: TaskManagerContext['resolveTask'],
  resolutions: ManualResolution[],
) => {
  openModal(
    closeModal => (
      <ResolveModal
        resolutions={resolutions}
        onSubmit={(reason, note) => resolveTask(task, reason, note)}
        closeModal={closeModal}
      />
    ),
    { closeOnBackgroundClick: false },
  );
};

type TaskActionColumnProps = {
  task: Task;
  loggedInPerson: Person;
  handleActionButtonClick: (task: Task, event: SyntheticEvent) => void;
  resolveTask: (task: Task, resolution: ManualResolution) => Promise<FetchResult<unknown>>;
  resolveTaskLoading: boolean;
  isClientTaskManager?: boolean;
};

const TaskActionColumn = ({
  task,
  loggedInPerson,
  handleActionButtonClick,
  resolveTask,
  resolveTaskLoading,
  isClientTaskManager,
}: TaskActionColumnProps) => {
  const jobType = task.job?.type;

  const taskAssignedToViewer = loggedInPerson.id === task.assignedPerson?.id;
  const actionPending =
    task.status === TaskStatus.PENDING_WORK && hasTaskAction(task.job?.type, task.type);

  const { getManualTaskResolutions } = useTaskManagerContext();
  const manualTaskResolutions = getManualTaskResolutions(jobType, task.type, task.assigneeType);
  const hasManualResolution = manualTaskResolutions?.length > 0;

  const showResolutionButton = taskAssignedToViewer && hasManualResolution;
  const showGoButton = (taskAssignedToViewer || !task.assignedPerson?.id) && actionPending;

  const openModal: React.MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    showResolveModal(task, resolveTask, manualTaskResolutions);
  };

  return (
    <Column>
      {!isClientTaskManager && showGoButton ? (
        <ActionButton
          aria-label={`Complete ${jobType} ${task.type} task`}
          onClick={event => {
            event.stopPropagation();
            handleActionButtonClick(task, event);
          }}
        >
          Go
        </ActionButton>
      ) : undefined}
      {showResolutionButton ? (
        <ActionButtonOutline disabled={resolveTaskLoading} onClick={openModal}>
          {'Resolve'}
        </ActionButtonOutline>
      ) : undefined}
    </Column>
  );
};

export default TaskActionColumn;
